import { useState } from 'react';
import { Segmented, Upload } from 'antd';
import { FileUp } from 'lucide-react';

import { Button, Field, Select, Textarea } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { NHAN_LOAI_TEP } from '../constants';
import { useNhapTepLicense } from '../hooks/useLicenseCucBo';
import { doanLoaiTep, kiemDangJws, TEP_TOI_DA_BYTE } from '../nghiep-vu/license-cuc-bo';
import type { LoiDanJws } from '../nghiep-vu/license-cuc-bo';
import type { LoaiTepNhap } from '../types';
import { DongThuocTinh } from '../../license/components/NoiDungLicense';

const CAC_LOAI: readonly LoaiTepNhap[] = ['license', 'keyring', 'revocation'];

/**
 * Nhập license, keyring hoặc danh sách thu hồi (ADR-0058 mục 6.1, `POST
 * /internal/v1/license/import`). Loại tệp đoán từ `typ` của JWS để người quản
 * trị không phải biết ba loại khác nhau ra sao; License Agent kiểm chữ ký rồi
 * mới lưu, nên tệp sai không làm hỏng license đang chạy.
 *
 * Khách offline nhận license qua email hay tin nhắn có thể dán thẳng nội dung
 * (một JWS compact) thay vì chép tệp sang máy; cả hai đi chung một API.
 */
export function KhoiNhapTep() {
  const { t } = useT();
  const dichLoi = useLoi();
  const nhap = useNhapTepLicense();

  const [tep, setTep] = useState<File | null>(null);
  const [loai, setLoai] = useState<LoaiTepNhap | null>(null);
  const [daDoan, setDaDoan] = useState(false);
  const [loiTep, setLoiTep] = useState<string | undefined>();
  const [cachNhap, setCachNhap] = useState<'tep' | 'dan'>('tep');
  const [noiDungDan, setNoiDungDan] = useState('');
  const [loiDan, setLoiDan] = useState<string | undefined>();

  const cauLoiDan = (l: LoiDanJws): string => {
    switch (l.ma) {
      case 'RONG':
        return t('licenseCucBo.loiChuaDan');
      case 'QUA_LON':
        return t('licenseCucBo.loiDanLon');
      case 'SAI_DANG':
        return t('licenseCucBo.loiDanSaiDang');
    }
  };

  const datLai = () => {
    setTep(null);
    setNoiDungDan('');
    setLoai(null);
    setDaDoan(false);
    setLoiTep(undefined);
    setLoiDan(undefined);
  };

  const doiCachNhap = (moi: 'tep' | 'dan') => {
    nhap.reset();
    datLai();
    setCachNhap(moi);
  };

  /** Dán xong là kiểm hình dạng và đoán loại ngay, như chọn tệp. */
  const danNoiDung = (vanBan: string) => {
    nhap.reset();
    setNoiDungDan(vanBan);
    setLoiTep(undefined);
    setLoiDan(undefined);
    if (vanBan.trim() === '') {
      setLoai(null);
      setDaDoan(false);
      return;
    }
    const kq = kiemDangJws(vanBan);
    if (!kq.ok) {
      setLoai(null);
      setDaDoan(false);
      setLoiDan(cauLoiDan(kq.loi));
      return;
    }
    const doan = doanLoaiTep(kq.jws);
    setLoai(doan);
    setDaDoan(doan !== null);
    if (doan === null) setLoiTep(t('licenseCucBo.loiKhongNhanRa'));
  };

  const chonTep = async (file: File) => {
    nhap.reset();
    setLoiTep(undefined);
    setTep(file);
    if (file.size > TEP_TOI_DA_BYTE) {
      setLoai(null);
      setDaDoan(false);
      setLoiTep(t('licenseCucBo.loiTepLon'));
      return;
    }
    const doan = doanLoaiTep(await file.text());
    setLoai(doan);
    setDaDoan(doan !== null);
    if (doan === null) setLoiTep(t('licenseCucBo.loiKhongNhanRa'));
  };

  const xacNhanDan = () => {
    const kq = kiemDangJws(noiDungDan);
    if (!kq.ok) {
      setLoiDan(cauLoiDan(kq.loi));
      return;
    }
    if (!loai) {
      setLoiTep(t('licenseCucBo.loiKhongNhanRa'));
      return;
    }
    nhap.mutate({ loai, noiDung: kq.jws }, { onSuccess: datLai });
  };

  const xacNhan = () => {
    if (cachNhap === 'dan') {
      xacNhanDan();
      return;
    }
    if (!tep) {
      setLoiTep(t('licenseCucBo.loiChuaChonTep'));
      return;
    }
    if (tep.size > TEP_TOI_DA_BYTE) {
      setLoiTep(t('licenseCucBo.loiTepLon'));
      return;
    }
    if (!loai) {
      setLoiTep(t('licenseCucBo.loiKhongNhanRa'));
      return;
    }
    nhap.mutate({ loai, noiDung: tep }, { onSuccess: datLai });
  };

  return (
    <div className="space-y-3">
      <Segmented<'tep' | 'dan'>
        aria-label={t('licenseCucBo.cachNhapNhan')}
        value={cachNhap}
        onChange={doiCachNhap}
        options={[
          { value: 'tep', label: t('licenseCucBo.cachNhapTep') },
          { value: 'dan', label: t('licenseCucBo.cachNhapDan') },
        ]}
      />

      {cachNhap === 'tep' ? (
        <Upload.Dragger
          maxCount={1}
          showUploadList={false}
          beforeUpload={(file) => {
            void chonTep(file);
            return false;
          }}
        >
          <div className="flex flex-col items-center gap-2 px-4 py-5">
            <FileUp size={26} aria-hidden className="text-content-muted" />
            <p className="text-sm font-medium text-content-primary">{t('licenseCucBo.keoTha')}</p>
            <p className="text-xs text-content-muted">{t('licenseCucBo.keoThaMo')}</p>
          </div>
        </Upload.Dragger>
      ) : (
        <Field
          label={t('licenseCucBo.noiDungDan')}
          required
          hint={t('licenseCucBo.noiDungDanGoiY')}
          error={loiDan}
        >
          {(p) => (
            <Textarea
              {...p}
              rows={5}
              spellCheck={false}
              autoComplete="off"
              className="font-mono text-xs"
              value={noiDungDan}
              placeholder={t('licenseCucBo.noiDungDanViDu')}
              onChange={(e) => danNoiDung(e.target.value)}
            />
          )}
        </Field>
      )}

      {cachNhap === 'tep' && tep && (
        <div className="rounded border border-default px-3 py-1">
          <DongThuocTinh nhan={t('licenseCucBo.dongTep')}>
            <span className="break-all">{tep.name}</span>
          </DongThuocTinh>
        </div>
      )}

      <Field
        label={t('licenseCucBo.loaiTepNhan')}
        hint={daDoan ? t('licenseCucBo.loaiTepGoiY') : undefined}
        error={loiTep}
      >
        {(p) => (
          <Select<LoaiTepNhap>
            {...p}
            className="w-full"
            value={loai ?? undefined}
            onChange={(v) => {
              setLoai(v);
              const nguonHopLe =
                cachNhap === 'tep' ? tep !== null && tep.size <= TEP_TOI_DA_BYTE : kiemDangJws(noiDungDan).ok;
              if (loiTep && nguonHopLe) setLoiTep(undefined);
            }}
            options={CAC_LOAI.map((l) => ({ value: l, label: t(NHAN_LOAI_TEP[l]) }))}
          />
        )}
      </Field>

      {nhap.error && (
        <div
          role="alert"
          className="rounded border border-danger-200 bg-danger-50 px-3 py-2.5 text-xs leading-relaxed text-danger-700"
        >
          {dichLoi(nhap.error)}
        </div>
      )}

      <div className="flex justify-end">
        <Button variant="primary" size="md" loading={nhap.isPending} onClick={xacNhan}>
          {nhap.isPending
            ? t('licenseCucBo.dangNhap')
            : t(cachNhap === 'tep' ? 'licenseCucBo.nhanNhap' : 'licenseCucBo.nhanNhapDan')}
        </Button>
      </div>
    </div>
  );
}
